import React from "react";

import { SubmissionForm } from "@bps/fluent-ui";
import { UseFormMethods } from "@bps/form-api/dist/src/lib/form-api.types";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import { SoftwarePackageInstallType } from "@libs/api/gateways/field/field-ops-gateway.dtos";
import {
  useCreateSoftwarePackageDefault,
  useUpdateSoftwarePackageDefault
} from "@libs/api/gateways/field/field-ops-gateway.hooks";
import { Permissions } from "@libs/permissions/permissions.enum";

import { SoftwarePackageDefaultSummaryDto } from "./";
import { SoftwarePackageDefaultInstallTypeDropDown } from "./SoftwarePackageDefaultInstallTypeDropDown";

interface SoftwarePackageDefaultInstallTypeFormValues {
  installType: SoftwarePackageInstallType;
}

interface SoftwarePackageDefaultInstallTypeFormProps {
  softwarePackage: SoftwarePackageDefaultSummaryDto;
}

export const SoftwarePackageDefaultInstallTypeForm: React.FC<SoftwarePackageDefaultInstallTypeFormProps> = ({
  softwarePackage
}) => {
  const { data: hasDeployPermission } = usePermissionsCheckQuery(
    Permissions.PltFieldManagementDeploy
  );

  const {
    mutateAsync: updateSoftwarePackageDefault
  } = useUpdateSoftwarePackageDefault();

  const {
    mutateAsync: createSoftwarePackageDefault
  } = useCreateSoftwarePackageDefault();

  const defaultInstallType = SoftwarePackageInstallType.All;

  const onSubmit = async (
    formValues: SoftwarePackageDefaultInstallTypeFormValues,
    actions: Pick<
      UseFormMethods<SoftwarePackageDefaultInstallTypeFormValues>,
      "reset" | "setValue"
    >
  ) => {
    const { installType } = formValues;
    const {
      id,
      softwarePackageId,
      softwarePackageVersionId,
      eTag,
      changeLog
    } = softwarePackage;

    const request = {
      id,
      softwarePackageId,
      softwarePackageVersionId,
      installType: installType ?? softwarePackage.installType,
      changeLog,
      eTag
    };

    if (softwarePackage.id === undefined) {
      // Create a software package default for the service
      try {
        await createSoftwarePackageDefault(request);
        actions.reset({ installType });
      } catch (e) {
        return e;
      }
    } else {
      // Update a software package default for the service
      try {
        await updateSoftwarePackageDefault(request);
        actions.reset({ installType });
      } catch (e) {
        return e;
      }
    }
  };

  return (
    <SubmissionForm<SoftwarePackageDefaultInstallTypeFormValues>
      initialValues={{
        installType: softwarePackage.installType ?? defaultInstallType
      }}
      onSubmit={onSubmit}
      hideButtons
      disabled={!hasDeployPermission}
    >
      {(_, actions) => (
        <SoftwarePackageDefaultInstallTypeDropDown
          softwarePackage={softwarePackage}
          onUpdate={installType => {
            actions.setValue("installType", installType);
            actions.submit();
          }}
        />
      )}
    </SubmissionForm>
  );
};
